import { useState } from 'react';
import { Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../theme/chart';
import { useDashboardCategories } from '../../../hooks/useDashboardCategories';
import type { CategorySpend } from './dashboard.types';
import { Meter, Panel, PanelHeader, Quiet } from './ui';

const SHOWN = { phone: 4, desk: 6 };

/**
 * "Para onde foi o dinheiro": one row per category, the amount and its share
 * of the month, the bar relative to the largest. Long lists fold behind
 * "Ver todas as categorias".
 */
export function CategoryBudgetBars({ categories, currency, title }: { categories: CategorySpend[]; currency: string; title?: string }) {
  const { t, i18n } = useTranslation('dashboard');
  const { getDashboardCategoryName } = useDashboardCategories();
  const [expanded, setExpanded] = useState(false);
  const money = (value: number) => formatMoney(value, currency, i18n.language);
  const sorted = [...categories].filter((category) => category.spent > 0).sort((a, b) => b.spent - a.spent);
  const total = sorted.reduce((sum, category) => sum + category.spent, 0);
  const max = Math.max(...sorted.map((category) => category.spent), 1);
  const heading = title ?? t('home.whereMoneyWent');

  return (
    <Panel label={heading}>
      <PanelHeader title={heading} aside={total > 0 ? money(total) : undefined} />
      {sorted.length === 0 && <Quiet>{t('home.noSpending')}</Quiet>}
      <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {sorted.map((category, index) => {
          const share = total > 0 ? Math.round((category.spent / total) * 100) : 0;
          // Folded rows: past 4 on a phone, past 6 on a desk, until expanded.
          const hidden = expanded
            ? undefined
            : { xs: index >= SHOWN.phone ? 'none' : 'flex', md: index >= SHOWN.desk ? 'none' : 'flex' };
          return (
            <Box component='li' key={category.key} sx={{ display: hidden ?? 'flex', flexDirection: 'column', gap: 0.6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 14 }}>
                <span>{getDashboardCategoryName('expense', category.key)}</span>
                <Box component='span' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <strong>{money(category.spent)}</strong>{' '}
                  <Box component='span' sx={{ color: 'text.secondary' }}>· {share}%</Box>
                </Box>
              </Box>
              <Meter ratio={category.spent / max} />
            </Box>
          );
        })}
      </Box>
      {!expanded && sorted.length > SHOWN.phone && (
        <Link
          component='button'
          type='button'
          underline='none'
          onClick={() => setExpanded(true)}
          sx={{ alignSelf: 'flex-start', fontSize: 13, fontWeight: 600, display: { xs: 'inline', md: sorted.length > SHOWN.desk ? 'inline' : 'none' } }}
        >
          {t('home.allCategories')}
        </Link>
      )}
    </Panel>
  );
}
